import { ENDPOINTS } from '@shared/constants';

import { EndpointParams } from './modules/types';

const BASE_URLS = {
  ASSETS: {
    IMAGES: `/assets/images`,
  },
  AUTH: `/auth`,
  BLOG: `/articles-blog`,
  PUBLIC_BLOG: `/public-articles-blog`,
  CONNECTED_APPS: `/connected-apps`,
  CONNECTIONS: `/connections`,
  CONSULTATIONS: '/consultations',
  CONSULTATION_CONFIGURATION: '/consultation-configurations',
  PUBLIC_CONSULTATIONS: '/public-consultations',
  CASE_STUDIES: '/articles-case-studies',
  PUBLIC_CASE_STUDIES: `/public-articles-case-studies`,
  EVENTS: `/articles-events`,
  PUBLIC_EVENTS: `/public-articles-events`,
  IDENTITIES: `/identities`,
  INTEGRATIONS: {
    ENDPOINTS: `/integrations/endpoints`,
    MAPPING: `/integrations/mappings`,
  },
  JOB_QUEUE: `/job-queue`,
  MODULES: `/modules`,
  NOTIFICATION: `/notifications`,
  NEWS: '/articles-news',
  PUBLIC_NEWS: `/public-articles-news`,
  PROPOSALS: '/proposals',
  PROPOSAL_CONFIGURATION: '/proposal-configurations',
  PUBLIC_PROPOSALS: '/public-proposals',
  SUBSCRIBERS: '/subscribers',
  PUBLIC_SUBSCRIBERS: '/public-subscribers',
  PERSONALIZATION: `/personalization`,
  CONFIGURABLE_FIELDS: {
    VALIDATIONS: `/configurable-fields/validations`,
  },
  REFERENCES: {
    REFERENCE_CATEGORIES: `/reference-categories`,
    REFERENCES: `/references`,
    IAM_REFERENCE_CATEGORIES: `/iam/reference-categories`,
    IAM_REFERENCES: `/iam/references`,
    EXTERNAL_REFERENCE_CATEGORIES: `/external/reference-categories`,
    EXTERNAL_REFERENCES: `/external/references`,
    CLIENT_REFERENCE_CATEGORIES: `/client/reference-categories`,
    CLIENT_REFERENCES: `/client/references`,
    TEMPLATES: `/templates`,
    FORMULA: `/formula`,
    PUBLIC_CATEGORIES_PUBLIC_SINGLE: `/public-refs`,
  },
  SETUP: {
    AUTHORIZERS: `/setup/iam-config/authorizers`,
    IAM_HANDSHAKE: `/setup/iam-config/handshake`,
    BACKUPS: `/setup/backups`,
    BACKUP_SCHEDULES: `/setup/backup-schedules`,
    LANGUAGES: `/setup/languages`,
    REFERENCE_MAPPINGS: `/setup/reference-mappings`,
    CURRENCIES: `/setup/currencies`,
    EXTERNAL_SYSTEMS: `/setup/external-systems`,
    EXTERNAL_SYSTEMS_KEYS: `/setup/external-systems/keys`,
  },
  STORAGE: {
    INTERNAL: '/internal/storage',
    INTERNAL_ENTITY: '/internal/storage/entity',
    INTERNAL_SHARE_INTERNALLY: '/internal/storage/share/internal',
  },
  SUBMISSIONS: `/submissions`,
  VACANCIES: `/vacancies`,
  PUBLIC_VACANCIES_PUBLIC_ACTIVE_ALL: `/public-vacancies`,
  PUBLIC_VACANCIES_GET_VIEW_COUNT: `/public-vacancies`,
  PUBLIC_SUBMISSION_PUBLIC_CREATE_SUBMISSION: '/public-submissions',
  PROJECT_MANAGEMENT: '/project-management',
  ROOMS: `/rooms`,
  HOLIDAY: '/holiday',
  INVENTORY: '/inventory',
  EMPLOYEE: `/employee`,
  BOOKINGS: `/bookings`,
  WORKSTATIONS: '/workstations',
  WORKSTATION_CONFIGURATION: '/workstation-configurations',
  SUPPORT_PROJECT: `/support/projects`,
  SUPPORT_TICKETS: `/support/tickets`,
  SUPPORT_RELEASE: `/support/releases`,
  ATTENDANCE: `/attendance`,
  ATTENDANCE_CONFIGURATION: `/attendance-configurations`,
  ATTENDANCE_BULK_UPLOAD: `/attendance-bulk-upload`,
};

/**
 *
 * NOTE: if you get an error here that might be due to missing cases.
 * Check whether you have added all the ENDPOINTS to cases in below switch or not
 *
 */
const getURLPath = (endpoint: EndpointParams): string => {
  switch (endpoint.endpoint) {
    // ------------------------------------------------------ GENERAL ENDPOINTS ------------------------------------------------------ //

    // AUTH
    case ENDPOINTS.AUTH_REFRESH_TOKEN:
      return `${BASE_URLS.AUTH}/token`;

    case ENDPOINTS.AUTH_LOGIN:
      return `${BASE_URLS.AUTH}/login`;

    case ENDPOINTS.AUTH_PROFILE_IMAGE_UPLOAD:
      return `${BASE_URLS.AUTH}/upload/profile-image`;

    case ENDPOINTS.AUTH_LOGIN_IMPERSONATE:
      return `${BASE_URLS.AUTH}/impersonate/login`;

    case ENDPOINTS.AUTH_CHECK_USER:
      return `${BASE_URLS.AUTH}/check`;

    case ENDPOINTS.AUTH_LOGOUT:
      return `${BASE_URLS.AUTH}/logout`;

    case ENDPOINTS.AUTH_FORGOT_PASSWORD:
      return `${BASE_URLS.AUTH}/password/forgot`;

    case ENDPOINTS.AUTH_VALIDATE_TOKEN:
      return `${BASE_URLS.AUTH}/token/validate`;

    case ENDPOINTS.AUTH_RESET_PASSWORD:
      return `${BASE_URLS.AUTH}/password/reset`;

    case ENDPOINTS.AUTH_REGISTER:
      return `${BASE_URLS.AUTH}/register`;

    case ENDPOINTS.AUTH_VERIFY:
      return `${BASE_URLS.AUTH}/verify`;

    case ENDPOINTS.AUTH_PROFILE:
      return `${BASE_URLS.AUTH}/profile`;

    case ENDPOINTS.AUTH_SEND_OTP:
      return `${BASE_URLS.AUTH}/otp/send`;

    //BLOG
    case ENDPOINTS.ARTICLE_BLOG_CREATE_NEW:
    case ENDPOINTS.ARTICLE_BLOG_GET_ALL:
      return BASE_URLS.BLOG;
    case ENDPOINTS.ARTICLE_BLOG_CREATE_DRAFT:
      return `${BASE_URLS.BLOG}/draft`;
    case ENDPOINTS.ARTICLE_BLOG_PERMALINK:
      return `${BASE_URLS.BLOG}/permalink`;
    case ENDPOINTS.ARTICLE_BLOG_GET_ONE:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_BLOG_UPDATE_ONE:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_BLOG_PUBLISH:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}/publish`;
    case ENDPOINTS.ARTICLE_BLOG_CREATE_PUBLISH:
      return `${BASE_URLS.BLOG}/publish`;
    case ENDPOINTS.ARTICLE_BLOG_ARCHIVE:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}/archive`;
    case ENDPOINTS.ARTICLE_BLOG_RESTORE:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}/restore`;
    case ENDPOINTS.ARTICLE_BLOG_DELETE:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_BLOG_DELETE_PERMANENT:
      return `${BASE_URLS.BLOG}/${endpoint.params.id}/permanent`;

    // BLOG - PUBLIC
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_GET_ALL:
      return BASE_URLS.PUBLIC_BLOG;
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_GET_ALL_FEATURED:
      return `${BASE_URLS.PUBLIC_BLOG}/featured`;
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_GET_ALL_NON_FEATURED:
      return `${BASE_URLS.PUBLIC_BLOG}/non-featured`;
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_GET_ALL_CATEGORIES:
      return `${BASE_URLS.PUBLIC_BLOG}/categories`;
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_GET_BY_SLUG:
    case ENDPOINTS.PUBLIC_ARTICLE_BLOG_UPDATE_BY_SLUG:
      return `${BASE_URLS.PUBLIC_BLOG}/${endpoint.params.slug}`;
    case ENDPOINTS.AUTH_USERNAME:
      return `${BASE_URLS.AUTH}/username`;

    case ENDPOINTS.AUTH_CODE:
      return `${BASE_URLS.AUTH}/code`;

    // CONNECTED - APPS
    case ENDPOINTS.CONNECTED_APPS_CONNECT_NEW_APPS:
    case ENDPOINTS.CONNECTED_APPS_GET_ALL_APPS:
      return BASE_URLS.CONNECTED_APPS;

    case ENDPOINTS.CONNECTED_APPS_GET_ONE_APP:
    case ENDPOINTS.CONNECTED_APPS_UPDATE_ONE_APP:
    case ENDPOINTS.CONNECTED_APPS_DELETE_ONE_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_FIELDS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/fields`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_ROLES_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/roles`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_PERMISSIONS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/permissions`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_DOCUMENTATIONS_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/docs`;

    case ENDPOINTS.CONNECTED_APPS_CONNECT_OR_DISCONNECT_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/${endpoint.params.status_change}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_REFERENCES_OF_A_CONNECTED_APP:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/reference-categories/${endpoint.params.categoryId}/references`;

    case ENDPOINTS.CONNECTED_APPS_HANDSHAKE:
      return `${BASE_URLS.CONNECTED_APPS}/handshake`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_SECRETS:
      return `${BASE_URLS.CONNECTED_APPS}/generate-secrets`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_PUBLIC_API_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/generate`;

    case ENDPOINTS.CONNECTED_APPS_GENERATE_PUBLIC_CLIENT_CREDENTIALS:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/generate-client-credentials`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_DELETED_CLIENT_SECRET:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/save-deleted-client-secret`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_DELETED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys/save-deleted-api-key`;

    case ENDPOINTS.CONNECTED_APPS_SAVE_GENERATED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys`;

    case ENDPOINTS.CONNECTED_APPS_UPDATE_GENERATED_PUBLIC_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_SPECIFIC_GENERATED_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_GET_ALL_GENERATED_KEYS:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.id}/keys`;

    case ENDPOINTS.CONNECTED_APPS_DELETE_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}`;

    case ENDPOINTS.CONNECTED_APPS_RESTORE_KEY:
      return `${BASE_URLS.CONNECTED_APPS}/${endpoint.params.appId}/keys/${endpoint.params.id}/restore`;

    //CONNECTIONS
    case ENDPOINTS.CONNECTIONS_GET_ALL:
      return BASE_URLS.CONNECTIONS;

    case ENDPOINTS.CONNECTIONS_UPDATE:
      return `${BASE_URLS.CONNECTIONS}/${endpoint.params.id}`;

    //CONSULTATIONS
    case ENDPOINTS.CONSULTATIONS_CREATE_NEW:
      return BASE_URLS.PUBLIC_CONSULTATIONS;
    case ENDPOINTS.CONSULTATIONS_GET_ALL:
      return BASE_URLS.CONSULTATIONS;
    case ENDPOINTS.CONSULTATIONS_SEND:
      return `${BASE_URLS.CONSULTATIONS}/send`;
    case ENDPOINTS.CONSULTATIONS_DELETE:
      return `${BASE_URLS.CONSULTATIONS}/${endpoint.params.id}`;
    case ENDPOINTS.CONSULTATIONS_RESTORE_ONE:
      return `${BASE_URLS.CONSULTATIONS}/${endpoint.params.id}/restore`;

    //CONSULTATION CONFIGURATION
    case ENDPOINTS.CONSULTATION_CONFIGURATIONS_CREATE_NEW:
    case ENDPOINTS.CONSULTATION_CONFIGURATIONS_GET_ALL:
      return BASE_URLS.CONSULTATION_CONFIGURATION;

    //CASE STUDIES
    case ENDPOINTS.ARTICLE_CASE_STUDIES_CREATE_NEW:
      return BASE_URLS.CASE_STUDIES;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_GET_ALL:
      return BASE_URLS.CASE_STUDIES;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_CREATE_DRAFT:
      return `${BASE_URLS.CASE_STUDIES}/draft`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_PERMALINK:
      return `${BASE_URLS.CASE_STUDIES}/permalink`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_GET_ONE:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_UPDATE_ONE:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_CREATE_PUBLISH:
      return `${BASE_URLS.CASE_STUDIES}/publish`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_PUBLISH:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}/publish`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_ARCHIVE:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}/archive`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_RESTORE:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}/restore`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_DELETE:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_CASE_STUDIES_DELETE_PERMANENT:
      return `${BASE_URLS.CASE_STUDIES}/${endpoint.params.id}/permanent`;

    // CASE STUDIES - PUBLIC
    case ENDPOINTS.PUBLIC_ARTICLE_CASE_STUDIES_GET_ALL:
      return BASE_URLS.PUBLIC_CASE_STUDIES;
    case ENDPOINTS.PUBLIC_ARTICLE_CASE_STUDIES_GET_BY_SLUG:
    case ENDPOINTS.PUBLIC_ARTICLE_CASE_STUDIES_UPDATE_BY_SLUG:
      return `${BASE_URLS.PUBLIC_CASE_STUDIES}/${endpoint.params.slug}`;

    // EVENTS
    case ENDPOINTS.ARTICLE_EVENTS_CREATE_NEW:
      return BASE_URLS.EVENTS;
    case ENDPOINTS.ARTICLE_EVENTS_CREATE_DRAFT:
      return `${BASE_URLS.EVENTS}/draft`;
    case ENDPOINTS.ARTICLE_EVENTS_GET_ALL:
      return BASE_URLS.EVENTS;
    case ENDPOINTS.ARTICLE_EVENTS_CREATE_PUBLISH:
      return `${BASE_URLS.EVENTS}/publish`;
    case ENDPOINTS.ARTICLE_EVENTS_GET_ONE:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_EVENTS_UPDATE_ONE:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_EVENTS_PUBLISH:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}/publish`;
    case ENDPOINTS.ARTICLE_EVENTS_ARCHIVE:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}/archive`;
    case ENDPOINTS.ARTICLE_EVENTS_RESTORE:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.ARTICLE_EVENTS_DELETE:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_EVENTS_DELETE_PERMANENT:
      return `${BASE_URLS.EVENTS}/${endpoint.params.id}/permanent`;

    // EVENTS - PUBLIC
    case ENDPOINTS.PUBLIC_ARTICLE_EVENTS_GET_ALL:
      return BASE_URLS.PUBLIC_EVENTS;

    //PROPOSALS
    case ENDPOINTS.PROPOSALS_GET_ALL:
      return BASE_URLS.PROPOSALS;
    case ENDPOINTS.PROPOSALS_SEND:
      return `${BASE_URLS.PROPOSALS}/send`;
    case ENDPOINTS.PROPOSALS_CREATE_NEW:
      return BASE_URLS.PUBLIC_PROPOSALS;
    case ENDPOINTS.PROPOSALS_DELETE:
      return `${BASE_URLS.PROPOSALS}/${endpoint.params.id}`;
    case ENDPOINTS.PROPOSALS_RESTORE:
      return `${BASE_URLS.PROPOSALS}/${endpoint.params.id}/restore`;

    //PROPOSAL CONFIGURATION
    case ENDPOINTS.PROPOSAL_CONFIGURATIONS_CREATE_NEW:
    case ENDPOINTS.PROPOSAL_CONFIGURATIONS_GET_ALL:
      return BASE_URLS.PROPOSAL_CONFIGURATION;

    //SUBSCRIBERS
    case ENDPOINTS.SUBSCRIBERS_GET_ALL:
      return BASE_URLS.SUBSCRIBERS;
    case ENDPOINTS.SUBSCRIBERS_CREATE_NEW:
      return BASE_URLS.PUBLIC_SUBSCRIBERS;
    case ENDPOINTS.SUBSCRIBERS_DELETE:
      return `${BASE_URLS.PUBLIC_SUBSCRIBERS}/${endpoint.params.id}`;
    case ENDPOINTS.SUBSCRIBERS_RESTORE:
      return `${BASE_URLS.SUBSCRIBERS}/${endpoint.params.id}/restore`;

    // VACANCIES
    case ENDPOINTS.VACANCIES_CREATE_NEW:
    case ENDPOINTS.VACANCIES_GET_ALL:
      return BASE_URLS.VACANCIES;
    case ENDPOINTS.VACANCIES_GET_REFERENCES:
      return `${BASE_URLS.VACANCIES}/referenceNumber`;
    case ENDPOINTS.VACANCIES_GET_ONE:
    case ENDPOINTS.VACANCY_DELETE_ONE:
      return `${BASE_URLS.VACANCIES}/${endpoint.params.id}`;
    case ENDPOINTS.VACANCY_RESTORE_ONE:
      return `${BASE_URLS.VACANCIES}/${endpoint.params.id}/restore`;
    case ENDPOINTS.PAUSE_RESTORE_ONE:
      return `${BASE_URLS.VACANCIES}/${endpoint.params.id}/pause`;

    case ENDPOINTS.CONTINUE_RESTORE_ONE:
      return `${BASE_URLS.VACANCIES}/${endpoint.params.id}/continue`;

    case ENDPOINTS.VACANCY_UPDATE_ONE:
      return `${BASE_URLS.VACANCIES}/vacancy/${endpoint.params.id}`;

    case ENDPOINTS.VACANCIES_GET_SUBMISSION_COUNT:
      return `${BASE_URLS.VACANCIES}/submissionCount/${endpoint.params.referenceNumber}`;

    // VACANCIES - PUBLIC
    case ENDPOINTS.VACANCIES_PUBLIC_GET_ALL_ACTIVE:
      return BASE_URLS.PUBLIC_VACANCIES_PUBLIC_ACTIVE_ALL;
    case ENDPOINTS.VACANCIES_PUBLIC_VIEW_COUNT:
      return `${BASE_URLS.PUBLIC_VACANCIES_GET_VIEW_COUNT}/${endpoint.params.referenceNumber}`;

    // IDENTITIES

    case ENDPOINTS.IDENTITIES_EL_ASYNC_GET:
      return `${BASE_URLS.IDENTITIES}/el-async`;

    case ENDPOINTS.IDENTITIES_HIERARCHICAL_EL_ASYNC:
      return `${BASE_URLS.IDENTITIES}/hierarchical-el-async`;

    case ENDPOINTS.IDENTITIES_ROLES_EL_ASYNC:
      return `${BASE_URLS.IDENTITIES}/roles-el-async`;

    case ENDPOINTS.IDENTITIES_SUBORDINATES:
      return `${BASE_URLS.IDENTITIES}/subordinates`;

    case ENDPOINTS.IDENTITIES_SUBORDINATE_IDS:
      return `${BASE_URLS.IDENTITIES}/subordinate-ids`;

    case ENDPOINTS.IDENTITIES_HIERARCHICAL:
      return `${BASE_URLS.IDENTITIES}/hierarchy`;

    case ENDPOINTS.IDENTITIES_GET_INTERNAL_FIELD:
      return `${BASE_URLS.IDENTITIES}/get-field-data`;

    case ENDPOINTS.IDENTITIES_GET_ONE:
      return `${BASE_URLS.IDENTITIES}/${endpoint.params.id}`;

    // INTEGRATION - ENDPOINTS

    case ENDPOINTS.INTEGRATION_ENDPOINTS_GET_ALL:
    case ENDPOINTS.INTEGRATION_ENDPOINTS_ADD_ONE:
      return BASE_URLS.INTEGRATIONS.ENDPOINTS;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_UPDATE_ONE:
    case ENDPOINTS.INTEGRATION_ENDPOINTS_DELETE_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}`;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_RESTORE_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.INTEGRATION_ENDPOINTS_VERIFY_ONE:
      return `${BASE_URLS.INTEGRATIONS.ENDPOINTS}/${endpoint.params.id}/verify`;

    // INTEGRATION -MAPPINGS
    case ENDPOINTS.INTEGRATION_MAPPINGS_ADD_ONE:
    case ENDPOINTS.INTEGRATION_MAPPINGS_GET_ALL:
      return BASE_URLS.INTEGRATIONS.MAPPING;
    case ENDPOINTS.INTEGRATION_MAPPINGS_UPDATE_ONE:
    case ENDPOINTS.INTEGRATION_MAPPINGS_DELETE_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}`;
    case ENDPOINTS.INTEGRATION_MAPPINGS_RESTORE_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}/restore`;
    case ENDPOINTS.INTEGRATION_MAPPINGS_VERIFY_ONE:
      return `${BASE_URLS.INTEGRATIONS.MAPPING}/${endpoint.params.id}/verify`;

    // JOB_QUEUE
    case ENDPOINTS.JOB_QUEUE_CHECK_STATUS:
      return `${BASE_URLS.JOB_QUEUE}/${endpoint.params.id}`;

    // MODULES
    case ENDPOINTS.MODULES_GET_ALL:
      return BASE_URLS.MODULES;

    case ENDPOINTS.MODULES_GET_ONE_BY_ID:
    case ENDPOINTS.MODULES_UPDATE:
      return `${BASE_URLS.MODULES}/${endpoint.params.id}`;

    case ENDPOINTS.MODULES_RESET:
      return `${BASE_URLS.MODULES}/${endpoint.params.id}/reset`;

    // PERSONALIZATION
    case ENDPOINTS.PERSONALIZATION_GET:
    case ENDPOINTS.PERSONALIZATION_UPDATE:
      return BASE_URLS.PERSONALIZATION;

    // NOTIFICATION
    case ENDPOINTS.NOTIFICATION_CERATE_WITHOUT_USER:
      return BASE_URLS.NOTIFICATION;

    case ENDPOINTS.NOTIFICATION_CREATE_MULTIPLE_NOTIFICATIONS_WITH_USERS:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_CERATE_WITH_USER:
      return `${BASE_URLS.NOTIFICATION}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_GET:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}`;

    case ENDPOINTS.NOTIFICATION_GET_USER_NOTIFICATIONS:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/user-notifications`;

    case ENDPOINTS.NOTIFICATION_FILTER:
      return BASE_URLS.NOTIFICATION;

    case ENDPOINTS.NOTIFICATIONS_MARK_AS_READ:
      return `${BASE_URLS.NOTIFICATION}/${endpoint.params.id}/read`;

    case ENDPOINTS.NOTIFICATIONS_MARK_ALL_AS_READ:
      return `${BASE_URLS.NOTIFICATION}/mark-all-read`;

    //NEWS
    case ENDPOINTS.ARTICLE_NEWS_CREATE_NEW:
      return BASE_URLS.NEWS;
    case ENDPOINTS.ARTICLE_NEWS_GET_ALL:
      return BASE_URLS.NEWS;
    case ENDPOINTS.ARTICLE_NEWS_CREATE_DRAFT:
      return `${BASE_URLS.NEWS}/draft`;
    case ENDPOINTS.ARTICLE_NEWS_PERMALINK:
      return `${BASE_URLS.NEWS}/permalink`;
    case ENDPOINTS.ARTICLE_NEWS_GET_ONE:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_NEWS_UPDATE_ONE:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_NEWS_PUBLISH:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}/publish`;
    case ENDPOINTS.ARTICLE_NEWS_CREATE_PUBLISH:
      return `${BASE_URLS.NEWS}/publish`;
    case ENDPOINTS.ARTICLE_NEWS_ARCHIVE:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}/archive`;
    case ENDPOINTS.ARTICLE_NEWS_RESTORE:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.ARTICLE_NEWS_DELETE:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}`;
    case ENDPOINTS.ARTICLE_NEWS_DELETE_PERMANENT:
      return `${BASE_URLS.NEWS}/${endpoint.params.id}/permanent`;

    // NEWS - PUBLIC
    case ENDPOINTS.PUBLIC_ARTICLE_NEWS_GET_ALL:
      return BASE_URLS.PUBLIC_NEWS;
    case ENDPOINTS.PUBLIC_ARTICLE_NEWS_GET_BY_SLUG:
    case ENDPOINTS.PUBLIC_ARTICLE_NEWS_UPDATE_BY_SLUG:
      return `${BASE_URLS.PUBLIC_NEWS}/${endpoint.params.slug}`;

    //WORKSTATIONS
    //TODO: ADD ROUTING @ranul

    // CONFIGURABLE_FIELDS - VALIDATIONS
    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_NAME:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/name`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_PHONE_NUMBER:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/phone-number`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_ADDRESS:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/address`;

    case ENDPOINTS.CONFIGURABLE_FIELDS_VALIDATIONS_EMAIL:
      return `${BASE_URLS.CONFIGURABLE_FIELDS.VALIDATIONS}/email`;

    // PROJECT_MANAGEMENT
    case ENDPOINTS.PROJECT_GET_ONE:
      return `${BASE_URLS.PROJECT_MANAGEMENT}/${endpoint.params.id}`;
    case ENDPOINTS.PROJECT_GET_ALL:
      return `${BASE_URLS.PROJECT_MANAGEMENT}`;
    case ENDPOINTS.PROJECT_UPDATE_ONE:
      return `${BASE_URLS.PROJECT_MANAGEMENT}/project/${endpoint.params.id}`;
    case ENDPOINTS.PROJECT_DELETE_ONE:
      return `${BASE_URLS.PROJECT_MANAGEMENT}/${endpoint.params.id}`;
    case ENDPOINTS.PROJECT_RESTORE_ONE:
      return `${BASE_URLS.PROJECT_MANAGEMENT}/${endpoint.params.id}/restore`;
    case ENDPOINTS.PROJECT_GET_OVERVIEW:
      return `${BASE_URLS.PROJECT_MANAGEMENT}/${endpoint.params.id}/overview`;

    // REFERENCES - PREDEFINED_REFERENCES
    case ENDPOINTS.PREDEFINED_REFERENCES_GET_VALUE:
      return `${BASE_URLS.REFERENCES.REFERENCES}/predefined-value`;

    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.REFERENCE_CATEGORIES_GET_ALL:
    case ENDPOINTS.REFERENCE_CATEGORIES_ADD_NEW:
      return BASE_URLS.REFERENCES.REFERENCE_CATEGORIES;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_EXTERNAL:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/external/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_GET_EXTERNAL_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/external/delete/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_UPDATE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}`;

    case ENDPOINTS.REFERENCE_CATEGORIES_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/delete`;

    case ENDPOINTS.REFERENCE_CATEGORIES_RESTORE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/restore`;

    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_ADD_NEW:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_GET_ALL_V2:
      return `/v2${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;
    // REFERENCES - REFERENCES
    case ENDPOINTS.REFERENCE_EDIT:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}`;

    case ENDPOINTS.REFERENCE_DELETE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}/delete`;

    case ENDPOINTS.REFERENCE_RESTORE:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.categoryId}/references/${endpoint.params.referenceId}/restore`;

    case ENDPOINTS.REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;

    // REFERENCES - BULK ADD
    case ENDPOINTS.REFERENCE_CATEGORIES_REFERENCES_BULK_ADD:
      return `${BASE_URLS.REFERENCES.REFERENCE_CATEGORIES}/${endpoint.params.id}/references/bulk`;

    // REFERENCES - PUBLIC
    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category`;

    case ENDPOINTS.REFERENCE_VACANCIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/vacancies`;

    case ENDPOINTS.REFERENCE_GENERAL_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/general`;

    case ENDPOINTS.REFERENCE_SUBMISSIONS_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/submissions`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_BLOG_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/blog/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_SUBMISSION_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/submissions/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_CATEGORY:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_LOCATION:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/location/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_MODALITY:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/modality/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_VACANCY_TYPES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/types/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_ACTIVITY_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/proposals/activity/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_UNIVERSITY_REFERENCES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/proposals/university/${endpoint.params.id}/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_ALL_VACANCY_CATEGORIES:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/categories/references`;

    case ENDPOINTS.REFERENCE_CATEGORIES_PUBLIC_GET_GET_TO_KNOW_QUESTIONS_ANSWERS:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/answers/references`;

    case ENDPOINTS.REFERENCE_BLOG__CATEGORIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/blogs`;

    case ENDPOINTS.REFERENCE_CASE_STUDIES__CATEGORIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/case-studies`;

    case ENDPOINTS.REFERENCE_NEWS__CATEGORIES_PUBLIC_GET_SINGLE:
      return `${BASE_URLS.REFERENCES.PUBLIC_CATEGORIES_PUBLIC_SINGLE}/category/news`;

    // REFERENCES - TEMPLATES
    case ENDPOINTS.TEMPLATES_ADD_NEW:
      return BASE_URLS.REFERENCES.TEMPLATES;

    case ENDPOINTS.TEMPLATES_UPDATE_ONE:
    case ENDPOINTS.TEMPLATES_GET_ONE:
      return `${BASE_URLS.REFERENCES.TEMPLATES}/${endpoint.params.id}`;

    // REFERENCES - FORMULA
    case ENDPOINTS.FORMULA_ANALYZE:
      return `${BASE_URLS.REFERENCES.FORMULA}/analyze`;

    case ENDPOINTS.FORMULA_EVALUATE:
      return `${BASE_URLS.REFERENCES.FORMULA}/evaluate`;

    // STORAGE - INTERNAL
    case ENDPOINTS.STORAGE_INTERNAL_UPLOAD:
      return `${BASE_URLS.STORAGE.INTERNAL}/upload`;

    case ENDPOINTS.STORAGE_INTERNAL_DELETE:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}`;

    case ENDPOINTS.STORAGE_INTERNAL_PREVIEW:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/preview`;

    case ENDPOINTS.STORAGE_INTERNAL_DOWNLOAD:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/download`;

    case ENDPOINTS.STORAGE_INTERNAL_SHARE_INTERNALLY:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/share/internal`;

    case ENDPOINTS.STORAGE_INTERNAL_CONTENT:
      return `${BASE_URLS.STORAGE.INTERNAL_ENTITY}/${endpoint.params.entityId}/content`;

    // SUBMISSIONS
    case ENDPOINTS.SUBMISSIONS_CREATE_NEW:
    case ENDPOINTS.SUBMISSIONS_GET_ALL:
      return BASE_URLS.SUBMISSIONS;
    case ENDPOINTS.SUBMISSIONS_UPDATE:
      return `${BASE_URLS.SUBMISSIONS}/${endpoint.params.id}`;
    case ENDPOINTS.SUBMISSIONS_SEND_MESSAGE:
      return `${BASE_URLS.SUBMISSIONS}/send`;
    case ENDPOINTS.SUBMISSIONS_SEND_MAIL:
      return `${BASE_URLS.SUBMISSIONS}/send-notification`;
    case ENDPOINTS.SUBMISSIONS_SENT_MESSAGES:
      return `${BASE_URLS.SUBMISSIONS}/send/${endpoint.params.id}`;
    case ENDPOINTS.SUBMISSIONS_STATUS_CHANGE:
      return `${BASE_URLS.SUBMISSIONS}/status/${endpoint.params.id}`;
    case ENDPOINTS.SUBMISSIONS_RESTORE_ONE:
      return `${BASE_URLS.SUBMISSIONS}/${endpoint.params.id}/restore`;
    case ENDPOINTS.SUBMISSIONS_DELETE:
      return `${BASE_URLS.SUBMISSIONS}/${endpoint.params.id}`;

    // SUBMISSIONS - PUBLIC
    case ENDPOINTS.SUBMISSION_PUBLIC_CREATE_SUBMISSION:
      return BASE_URLS.PUBLIC_SUBMISSION_PUBLIC_CREATE_SUBMISSION;
    case ENDPOINTS.SUBMISSIONS_PUBLIC_GET_RESPONSE:
      return `${BASE_URLS.PUBLIC_SUBMISSION_PUBLIC_CREATE_SUBMISSION}/technical-interview/${endpoint.params.id}/response`;
    case ENDPOINTS.SUBMISSIONS_PUBLIC_GET_RESPONSE_HR:
      return `${BASE_URLS.PUBLIC_SUBMISSION_PUBLIC_CREATE_SUBMISSION}/hr-interview/${endpoint.params.id}/response`;
    // SETUP - REFERENCE_MAPPING
    case ENDPOINTS.SETUP_GET_ALL_REFERENCE_MAPPINGS:
      return BASE_URLS.SETUP.REFERENCE_MAPPINGS;

    case ENDPOINTS.SETUP_GET_REFERENCE_MAPPING_BY_KEY:
      return `${BASE_URLS.SETUP.REFERENCE_MAPPINGS}/${endpoint.params.key}`;

    case ENDPOINTS.SETUP_UPDATE_REFERENCE_MAPPING:
      return `${BASE_URLS.SETUP.REFERENCE_MAPPINGS}/${endpoint.params.id}`;

    // SETUP - BACKUP_SCHEDULES
    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_ADD_NEW:
      return BASE_URLS.SETUP.BACKUP_SCHEDULES;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_GET_ALL:
      return BASE_URLS.SETUP.BACKUP_SCHEDULES;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_DELETE_ONE:
      return `${BASE_URLS.SETUP.BACKUP_SCHEDULES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_BACKUPS_SCHEDULES_ENABLE_DISABLE_ONE:
      return `${BASE_URLS.SETUP.BACKUP_SCHEDULES}/${endpoint.params.id}/${endpoint.params.status}`;

    // SETUP - IAM
    case ENDPOINTS.SETUP_IAM_AUTHORIZERS_AZURE:
      return `${BASE_URLS.SETUP.AUTHORIZERS}/azure`;

    case ENDPOINTS.SETUP_IAM_HANDSHAKE:
      return `${BASE_URLS.SETUP.IAM_HANDSHAKE}`;

    // SETUP - BACKUPS
    case ENDPOINTS.SETUP_BACKUPS_BACKUP_NOW:
    case ENDPOINTS.SETUP_BACKUPS_GET_ALL:
      return BASE_URLS.SETUP.BACKUPS;

    case ENDPOINTS.SETUP_BACKUPS_DOWNLOAD_ONE:
    case ENDPOINTS.SETUP_BACKUPS_DELETE_ONE:
      return `${BASE_URLS.SETUP.BACKUPS}/${endpoint.params.name}`;

    // SETUP - LANGUAGES
    case ENDPOINTS.SETUP_LANGUAGES_ADD_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_GET_ALL:
      return BASE_URLS.SETUP.LANGUAGES;

    case ENDPOINTS.SETUP_LANGUAGES_GET_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_UPDATE_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_ENABLE_DISABLE_ONE:
    case ENDPOINTS.SETUP_LANGUAGES_DELETE_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_LANGUAGES_REFRESH_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}/refresh`;

    case ENDPOINTS.SETUP_LANGUAGES_GET_CONTENT_OF_ONE:
      return `${BASE_URLS.SETUP.LANGUAGES}/${endpoint.params.id}/content`;

    // SETUP - CURRENCIES
    case ENDPOINTS.SETUP_CURRENCIES_ADD_ONE:
    case ENDPOINTS.SETUP_CURRENCIES_GET_ALL:
      return BASE_URLS.SETUP.CURRENCIES;

    case ENDPOINTS.SETUP_CURRENCIES_GET_ONE:
    case ENDPOINTS.SETUP_CURRENCIES_UPDATE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_CURRENCIES_DELETE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/delete`;

    case ENDPOINTS.SETUP_CURRENCIES_RESTORE_ONE:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/restore`;

    case ENDPOINTS.SETUP_CURRENCIES_TOGGLE_BASE_CURRENCY:
      return `${BASE_URLS.SETUP.CURRENCIES}/${endpoint.params.id}/base_currency`;

    // ASSETS
    case ENDPOINTS.ASSETS_IMAGES_UPLOAD:
      return BASE_URLS.ASSETS.IMAGES;

    case ENDPOINTS.ASSETS_IMAGES_DELETE:
      return `${BASE_URLS.ASSETS.IMAGES}/${endpoint.params.name}`;

    // SETUP - EXTERNAL SYSTEMS
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_GET_ALL:
      return BASE_URLS.SETUP.EXTERNAL_SYSTEMS;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEM_GET_EXTERNAL_REFERENCE_CATEGORIES:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS}/${endpoint.params.id}/reference-categories`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_UPDATE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS}/${endpoint.params.id}`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_SAVE_KEY:
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GET_ALL:
      return BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GENERATE_NEW_KEY:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/generate`;

    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_GET_ONE:
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_DELETE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/${endpoint.params.id}`;
    case ENDPOINTS.SETUP_EXTERNAL_SYSTEMS_KEYS_RESTORE_ONE:
      return `${BASE_URLS.SETUP.EXTERNAL_SYSTEMS_KEYS}/${endpoint.params.id}/restore`;

    // ------------------------------------------------------ GENERAL ENDPOINTS ------------------------------------------------------ //

    // BOOKINGS
    case ENDPOINTS.BOOKINGS_CREATE_NEW:
    case ENDPOINTS.BOOKINGS_GET_ALL:
      return BASE_URLS.BOOKINGS;
    case ENDPOINTS.BOOKINGS_UPDATE_ONE:
      return `${BASE_URLS.BOOKINGS}/${endpoint.params.id}`;
    case ENDPOINTS.BOOKINGS_GET_ONE:
      return `${BASE_URLS.BOOKINGS}/${endpoint.params.id}`;
    case ENDPOINTS.BOOKINGS_DELETE_ONE:
      return `${BASE_URLS.BOOKINGS}/delete/${endpoint.params.id}`;

    // ROOMS
    case ENDPOINTS.ROOMS_CREATE_NEW:
    case ENDPOINTS.ROOMS_GET_ALL:
      return BASE_URLS.ROOMS;
    case ENDPOINTS.ROOMS_UPDATE_ONE:
      return `${BASE_URLS.ROOMS}/${endpoint.params.id}`;
    case ENDPOINTS.ROOMS_GET_ONE:
      return `${BASE_URLS.ROOMS}/${endpoint.params.id}`;
    case ENDPOINTS.ROOMS_DELETE_ONE:
      return `${BASE_URLS.ROOMS}/delete/${endpoint.params.id}`;
    case ENDPOINTS.ROOMS_RESTORE_ONE:
      return `${BASE_URLS.ROOMS}/restore/${endpoint.params.id}`;

    //EMPLOYEE
    case ENDPOINTS.EMPLOYEE_CREATE_NEW:
    case ENDPOINTS.EMPLOYEE_GET_ALL:
      return BASE_URLS.EMPLOYEE;
    case ENDPOINTS.EMPLOYEE_CAREER_UPDATE_ONE:
      return `${BASE_URLS.EMPLOYEE}/career/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEE_UPDATE_ONE:
      return `${BASE_URLS.EMPLOYEE}/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEE_GET_ONE:
      return `${BASE_URLS.EMPLOYEE}/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEE_GET_ONE_BY_EMPLOYEEID:
      return `${BASE_URLS.EMPLOYEE}/employeeId`;
    case ENDPOINTS.EMPLOYEE_GET_ONE_OVERVIEW:
      return `${BASE_URLS.EMPLOYEE}/${endpoint.params.id}/overview`;
    case ENDPOINTS.EMPLOYEE_GET_ONE_CAREER:
      return `${BASE_URLS.EMPLOYEE}/${endpoint.params.id}/career`;
    case ENDPOINTS.VALIDATE_EMPLOYEE:
      return `${BASE_URLS.EMPLOYEE}/validation`;
    case ENDPOINTS.EMPLOYEE_GET_ROLE_LIST:
      return `${BASE_URLS.EMPLOYEE}/${endpoint.params.id}/role`;
    case ENDPOINTS.DELETE_PROMOTION:
      return `${BASE_URLS.EMPLOYEE}/delete-promotion/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEE_DISABLE:
      return `${BASE_URLS.EMPLOYEE}/disable/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEE_ENABLE:
      return `${BASE_URLS.EMPLOYEE}/enable/${endpoint.params.id}`;

    //WORKSTATIONS
    case ENDPOINTS.WORKSTATIONS_CREATE_NEW:
    case ENDPOINTS.WORKSTATIONS_GET_ALL:
      return BASE_URLS.WORKSTATIONS;
    case ENDPOINTS.WORKSTATIONS_DELETE:
      return `${BASE_URLS.WORKSTATIONS}/delete`;
    case ENDPOINTS.WORKSTATIONS_HISTORY:
      return `${BASE_URLS.WORKSTATIONS}/history/${endpoint.params.date}`;

    //WORKSTATION CONFIGURATION
    case ENDPOINTS.WORKSTATION_CONFIGURATIONS_CREATE_NEW:
    case ENDPOINTS.WORKSTATION_CONFIGURATIONS_GET_ALL:
      return BASE_URLS.WORKSTATION_CONFIGURATION;
    case ENDPOINTS.WORKSTATION_CONFIGURATIONS_GET_ONE:
      return `${BASE_URLS.WORKSTATION_CONFIGURATION}/${endpoint.params.id}`;

    // HOLIDAY
    case ENDPOINTS.HOLIDAY_CREATE_NEW:
    case ENDPOINTS.HOLIDAY_GET_ALL:
      return BASE_URLS.HOLIDAY;
    case ENDPOINTS.HOLIDAY_BULK_UPLOAD:
      return `${BASE_URLS.HOLIDAY}/bulk-upload`;
    case ENDPOINTS.HOLIDAY_UPDATE_ONE:
      return `${BASE_URLS.HOLIDAY}/${endpoint.params.id}`;
    case ENDPOINTS.HOLIDAY_GET_ONE:
      return `${BASE_URLS.HOLIDAY}/${endpoint.params.id}`;
    case ENDPOINTS.HOLIDAY_DELETE_ONE:
      return `${BASE_URLS.HOLIDAY}/delete/${endpoint.params.id}`;
    case ENDPOINTS.HOLIDAY_RESTORE_ONE:
      return `${BASE_URLS.HOLIDAY}/restore/${endpoint.params.id}`;

    // INVENTORY
    case ENDPOINTS.INVENTORY_CREATE_NEW:
    case ENDPOINTS.INVENTORY_GET_ALL:
      return BASE_URLS.INVENTORY;
    case ENDPOINTS.INVENTORY_UPDATE_ONE:
      return `${BASE_URLS.INVENTORY}/${endpoint.params.id}`;
    case ENDPOINTS.INVENTORY_GET_ONE:
      return `${BASE_URLS.INVENTORY}/${endpoint.params.id}`;
    case ENDPOINTS.INVENTORY_DELETE_ONE:
      return `${BASE_URLS.INVENTORY}/delete/${endpoint.params.id}`;
    case ENDPOINTS.INVENTORY_RESTORE_ONE:
      return `${BASE_URLS.INVENTORY}/restore/${endpoint.params.id}`;

    // SUPPORT PROJECTS
    case ENDPOINTS.SUPPORT_PROJECTS_CREATE_NEW:
    case ENDPOINTS.SUPPORT_PROJECTS_GET_ALL:
      return BASE_URLS.SUPPORT_PROJECT;
    case ENDPOINTS.SUPPORT_PROJECTS_GET_ONE:
      return `${BASE_URLS.SUPPORT_PROJECT}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_PROJECTS_UPDATE_ONE:
      return `${BASE_URLS.SUPPORT_PROJECT}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_PROJECTS_DELETE_ONE:
      return `${BASE_URLS.SUPPORT_PROJECT}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_PROJECTS_RESTORE_ONE:
      return `${BASE_URLS.SUPPORT_PROJECT}/restore/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_PROJECTS_SEND_CCM:
      return `${BASE_URLS.SUPPORT_PROJECT}/send-project-ccm/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_PROJECTS_ASSIGN_CONTACT_PERSONS_CCM:
      return `${BASE_URLS.SUPPORT_PROJECT}/assign-project-contact-persons-ccm/${endpoint.params.id}`;

    // SUPPORT TICKETS
    case ENDPOINTS.SUPPORT_TICKETS_GET_ALL:
      return BASE_URLS.SUPPORT_TICKETS;
    case ENDPOINTS.SUPPORT_TICKETS_INTERNAL:
      return `${BASE_URLS.SUPPORT_TICKETS}/internal`;
    case ENDPOINTS.SUPPORT_TICKETS_CUSTOMER_CREATION_CCM:
      return `${BASE_URLS.SUPPORT_TICKETS}/send-customer-creation-ccm`;
    case ENDPOINTS.SUPPORT_TICKETS_INTERNAL_CREATION_CCM:
      return `${BASE_URLS.SUPPORT_TICKETS}/send-internal-creation-ccm`;
    case ENDPOINTS.SUPPORT_TICKETS_INTERNAL_RESPONSE_CCM:
      return `${BASE_URLS.SUPPORT_TICKETS}/send-internal-response-ccm`;
    case ENDPOINTS.SUPPORT_TICKETS_UPDATE_ONE:
      return `${BASE_URLS.SUPPORT_TICKETS}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_TICKETS_CUSTOMER_RESPONSE_CCM:
      return `${BASE_URLS.SUPPORT_TICKETS}/send-customer-response-ccm`;

    // SUPPORT RELEASES
    case ENDPOINTS.SUPPORT_RELEASES_CREATE_NEW:
    case ENDPOINTS.SUPPORT_RELEASES_GET_ALL:
      return BASE_URLS.SUPPORT_RELEASE;
    case ENDPOINTS.SUPPORT_RELEASES_GET_ONE:
      return `${BASE_URLS.SUPPORT_RELEASE}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_RELEASES_UPDATE_ONE:
      return `${BASE_URLS.SUPPORT_RELEASE}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_RELEASES_DELETE_ONE:
      return `${BASE_URLS.SUPPORT_RELEASE}/${endpoint.params.id}`;
    case ENDPOINTS.SUPPORT_RELEASES_SEND_CCM:
      return `${BASE_URLS.SUPPORT_RELEASE}/send-release-ccm/${endpoint.params.id}`;
    case ENDPOINTS.EMPLOYEES_GET_ALL:
      return `${BASE_URLS.SUPPORT_RELEASE}/employees`;

    // ATTENDANCE
    case ENDPOINTS.ATTENDANCE_CREATE_NEW:
      return BASE_URLS.ATTENDANCE;
    case ENDPOINTS.ATTENDANCE_UPDATE:
      return `${BASE_URLS.ATTENDANCE}/${endpoint.params.id}`;
    case ENDPOINTS.ATTENDANCE_GET_ALL:
      return BASE_URLS.ATTENDANCE;
    case ENDPOINTS.ATTENDANCE_FIND_ACTIVE_CLOCKINS:
      return `${BASE_URLS.ATTENDANCE}/check-active/${endpoint.params.id}`;
    case ENDPOINTS.ATTENDANCE_CLOCKIN_COUNT:
      return `${BASE_URLS.ATTENDANCE}/clockin-count/${endpoint.params.id}`;

    // ATTENDANCE CONFIGURATION
    case ENDPOINTS.ATTENDANCE_CONFIGURATIONS_CREATE_NEW:
    case ENDPOINTS.ATTENDANCE_CONFIGURATIONS_GET_ALL:
      return BASE_URLS.ATTENDANCE_CONFIGURATION;
    case ENDPOINTS.ATTENDANCE_CONFIGURATIONS_GET_ONE:
      return `${BASE_URLS.ATTENDANCE_CONFIGURATION}/${endpoint.params.id}`;

    // ATTENDANCE BULK UPLOAD
    case ENDPOINTS.ATTENDANCE_BULK_UPLOAD_CREATE_NEW:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload`;
    case ENDPOINTS.ATTENDANCE_BULK_UPLOAD_GET_ALL_DUPLICATES:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload`;
    case ENDPOINTS.ATTENDANCE_BULK_UPLOAD_GET_ALL_RECORDS:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload/get-all-records`;
    case ENDPOINTS.ATTENDANCE_BULK_UPLOAD_UPDATE_SELECTION:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload/update-selection`;
    case ENDPOINTS.ATTENDANCE_BULK_UPLOAD_ADD_AUTHORIZERS:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload/add-authorizers`;
    case ENDPOINTS.ATTENDANCE_UPDATE_SELECTION:
      return `${BASE_URLS.ATTENDANCE}/bulk-upload/update-selected-attendance`;

    // ATTENDANCE FILES
    case ENDPOINTS.ATTENDANCE_GET_ALL_FILES:
      return `${BASE_URLS.ATTENDANCE}/files`;
    case ENDPOINTS.ATTENDANCE_GET_FILE_DETAILS:
      return `${BASE_URLS.ATTENDANCE}/files/${endpoint.params.id}`;
    case ENDPOINTS.ATTENDANCE_GET_DUPLICATE_FOR_APPROVAL:
      return `${BASE_URLS.ATTENDANCE}/approval-duplicates/${endpoint.params.id}`;
    case ENDPOINTS.ATTENDANCE_GET_ALL_FOR_APPROVAL:
      return `${BASE_URLS.ATTENDANCE}/approval-all-records`;
    case ENDPOINTS.ATTENDANCE_ADD_APPROVAL_REQUEST_DECISION:
      return `${BASE_URLS.ATTENDANCE}/approval-request-decision/${endpoint.params.id}`;
    case ENDPOINTS.ATTENDANCE_APPROVAL_REQUEST_UPDATE_AUTHORIZERS:
      return `${BASE_URLS.ATTENDANCE}/approval-request/update-authorizers`;
    // ------------------------------------------------------ CLIENT ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.CLIENT_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.CLIENT_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.CLIENT_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.CLIENT_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ CLIENT ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ EXTERNAL ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.EXTERNAL_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.EXTERNAL_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.EXTERNAL_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.EXTERNAL_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ EXTERNAL ENDPOINTS ------------------------------------------------------ //

    // ------------------------------------------------------ IAM ENDPOINTS ------------------------------------------------------ //
    // REFERENCES - REFERENCE_CATEGORIES
    case ENDPOINTS.IAM_REFERENCE_CATEGORIES_REFERENCES_GET_ALL:
      return `${BASE_URLS.REFERENCES.IAM_REFERENCE_CATEGORIES}/${endpoint.params.id}/references`;

    // REFERENCES - REFERENCES
    case ENDPOINTS.IAM_REFERENCES_FIELDS_GET_VALUE:
      return `${BASE_URLS.REFERENCES.IAM_REFERENCES}/${endpoint.params.id}/fields/${endpoint.params.field_id}/value`;
    // ------------------------------------------------------ IAM ENDPOINTS ------------------------------------------------------ //
  }
};

export const generateURL = (endpoint: EndpointParams): string => {
  const path = getURLPath(endpoint);
  const apiVersion = endpoint.version ? `/v${endpoint.version}` : '';

  return `/api${apiVersion}${path}`;
};
